import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import type { User } from '@/lib/types';
import {
  USER_GROUP_SLUG_LIST,
  narrowStaffToGroup,
  openStaff,
  useUserGroups,
  type StaffNarrowing,
  type UserGroupSlug,
} from '@/lib/user-groups';

/**
 * Loads staff for the owner / account lead / client success manager pickers.
 *
 * One fetch of the first page at the API's maximum `perPage`, shared by every
 * picker on a form. The pickers are searchable comboboxes that filter this list
 * locally — instant, and correct as long as the whole directory fits one page.
 *
 * `hasMore` is how we avoid lying about that: when the directory outgrows a
 * page, the pickers surface a footnote rather than silently hiding people, and
 * that is the signal to move these to server-side search the way the company
 * picker already does.
 *
 * `failed` is the same principle one level up. Without it, a 500, a dropped
 * connection or an expired token would arrive at every owner / account-lead /
 * CSM picker in the app as "loaded, zero staff" — a statement about the
 * company, made on the strength of a request that never came back. Callers
 * must treat it as "we do
 * not know who is on staff" and render an error naming the failure, never an
 * innocent-looking empty list. Same shape as `useAdminOptions`,
 * `useReferenceOptions` and `useCompanyStages`, deliberately.
 *
 * ## The second argument: user groups
 *
 * `withGroups` opts a call site into the `/user-groups` master, and with it a
 * per-group view of the SAME directory page under `byGroup`. Off by default, so
 * every existing caller behaves exactly as it did and pays for nothing — a
 * second request no picker on the page can read is waste, and the rate limit it
 * spends from is per IP, shared across the office.
 * ⚠️ *SUPERSEDED: "the rate limit is 120 requests per 60 seconds per IP."* It is
 * **12,000/60s** (corrected 2026-09-17). Opting in rather than always fetching
 * is still right — the cost is a request nothing reads — but the budget that
 * reason cited is off by a factor of a hundred.
 *
 * ⚠️ **It is a boolean rather than one slug, because a form mounts several
 * pickers over ONE fetch.** `CompanyFormModal` has Owner (unnarrowed), Account
 * lead (`account_lead`) and Client success manager (`client_success_manager`)
 * side by side; a hook that narrowed `users` itself would have to be called
 * three times and would fetch `/users` three times. So `users` is left alone —
 * it is still the whole directory — and `byGroup[slug]` is the narrowed view a
 * picker renders. Owner pickers keep reading `users`: **`ownerId` is not
 * checked by the server and must not be narrowed.**
 */
export function useUserOptions(enabled = true, withGroups = false) {
  const [users, setUsers] = useState<User[]>([]);
  // Starts true when enabled so the first paint shows a loading picker rather
  // than an empty one — and so nothing calls setState synchronously in an effect.
  const [loading, setLoading] = useState(enabled);
  const [failed, setFailed] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    api
      .listUsers({ page: 1, perPage: 100 }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setUsers(result.data);
        setHasMore(result.meta.totalPages > 1);
        setFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setUsers([]);
        setFailed(true);
        /*
         * Cleared alongside the rows it described. Left standing it would put
         * "showing the first page of staff" beside a picker that holds no staff
         * at all, which reads as a truncation rather than a failure.
         */
        setHasMore(false);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  const groups = useUserGroups(enabled && withGroups);

  /*
   * One narrowing per slug, memoised so a call site can list `byGroup.x.users`
   * in a `useMemo` dependency list without it churning every render.
   *
   * EVERY slug is computed rather than only the ones asked for: the list is
   * short, the work is a filter over at most 100 rows, and a per-slug opt-in
   * would be one more thing for a call site to get wrong.
   *
   * ⚠️ This said "all three … there are three" until 2026-08-29, when
   * `approvers` made it four — a sentence that was true when written and became
   * a wrong count the day somebody added a group, with nothing able to fail.
   * It is deliberately worded without a number now: the loop reads
   * `USER_GROUP_SLUG_LIST`, so the count is a property of that list and never
   * of this comment. When `withGroups` is false the
   * master is never read, `groupBySlug` finds nothing, and every entry is the
   * un-narrowed directory with `narrowed: false` — which is precisely what an
   * un-narrowed picker wants.
   */
  const byGroup = useMemo(() => {
    const result = {} as Record<UserGroupSlug, StaffNarrowing>;
    for (const slug of USER_GROUP_SLUG_LIST) {
      result[slug] = withGroups
        ? narrowStaffToGroup(users, groups.records, slug)
        : openStaff(users);
    }
    return result;
  }, [users, groups.records, withGroups]);

  return {
    users,
    loading,
    failed,
    hasMore,
    /** Per-group views of `users`. See the note on `withGroups` above. */
    byGroup,
    /** True while the group master is still being read. */
    groupsLoading: groups.loading,
    /** The group master read failed — "we do not know", never "there are none". */
    groupsFailed: groups.failed,
    /** The group master answered 404: not set up on this API yet. */
    groupsUnavailable: groups.unavailable,
  };
}
